// frontend/src/components/RiskForecast.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';

export default function RiskForecast({ farmId, plotId, onSelectRiskModel }) {
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    //console.log(`[RiskForecast] farmId: ${farmId}, plotId: ${plotId}`);
    if (!farmId || !plotId) {
      setForecast(null);
      return;
    }

    const fetchForecast = async () => {
      setLoading(true);
      setError('');
      console.log("[RiskForecast] Load risk forecast...");
      try {
        const response = await axios.get(
          `/api/farms/${farmId}/plots/${plotId}/riskForecast`,
          { withCredentials: true }
        );
        setForecast(response.data);
        //console.log("[RiskForecast] Got forecast data:",response.data);
      } catch (err) {
        console.error('Failed to load risk forecast:', err);
        setError('Unable to load forecast data for this plot.');
      } finally {
        setLoading(false);
      }
    };

    fetchForecast();
  }, [farmId, plotId]);

  if (loading) return <div className="risk-forecast loading-panel">Loading 5-day risk forecast...</div>;
  if (error) return <div className="risk-forecast error-panel">{error}</div>;
  //console.log("[RiskForecast] forecast:",forecast);
  if (!forecast?.daily_risks) return null;

  const handleRiskClick = (date, modelType, riskLevel) => {
    if (onSelectRiskModel) {
      onSelectRiskModel({
        farmId,
        plotId,
        date,
        modelType,
        riskLevel
      });
    }
  };

  return (
    <div className="risk-forecast">
      <h3 className="risk-forecast__title">5-Day Agronomic Risk Forecast</h3>

      <div className="forecast-grid">
        {Object.entries(forecast.daily_risks || {})
           .slice(0, 5)
           .map(([date, day], idx) => {
          //console.log("[RiskForecast] Got date:",date);
          //console.log("[RiskForecast] Got day:",day);
          const diseaseRisk = day.disease_risk?.toLowerCase() || '--';
          const pestRisk = day.pest_risk?.toLowerCase() || '--';
          if (day.growth_stage == '-Inf') day.growth_stage = '--';

          return (
          <div key={date} className="forecast-day-card">
          <div className="forecast-day-card__header"> {date} </div>

          <div className="forecast-day-card__temp">
            <span className="temp-high">{Math.round(day.temp_max ?? 0)}°</span> /{' '}
            <span className="temp-low">{Math.round(day.temp_min ?? 0)}°C</span>
          </div>

          {/* Phenology / Crop Stage */}
          {day.growth_stage && (
            <div className="forecast-day-card__metric">
              Crop Stage
              <strong>{day.growth_stage}</strong>
            </div>
          )}

          {/* Hazard Alert */}
          {day.primary_hazard && day.primary_hazard !== 'None' && (
            <div className="forecast-day-card__metric" 
                 style={{ color: 'var(--status_bad_ink)' }}>
              <strong>Hazard:</strong> {day.primary_hazard}
            </div>
          )}

          {/* Daily Environmental Averages */}
          <div className="forecast-day-card__metric">
            Avg Humidity / Dew Pt
            <strong>
              {day.humidity != null ? `${Math.round(day.humidity)}%` : '--'} |{' '}
              {day.dewpoint != null ? `${Math.round(day.dewpoint)}°C` : '--'}
            </strong>
          </div>

          <div className="forecast-day-card__metric">
            Vapor Pressure Deficit
            <strong>{day.vpd != null ? `${Number(day.vpd).toFixed(2)} kPa` : '--'}</strong>
          </div>

          {/* Thermal Accumulation */}
          <div className="forecast-day-card__metric">
            Daily / Total Thermal
            <strong>
              {day.degree_hours != null ? `${Math.round(day.degree_hours)} GDH` : '--'} |{' '}
              {day.degree_days != null ? `${Math.round(day.degree_days)} GDD` : '--'}
            </strong>
          </div>

          {/* Clickable Risk Pills linking to PlotStatus model drill-down */}
          <button
            type="button"
            className={`risk-pill risk-pill--${diseaseRisk}`}
            onClick={() => handleRiskClick(day.date, 'disease', day.disease_risk)}
          >
            Disease: {day.disease_risk || "LOW (< 10%)"}
          </button>

          <button
            type="button"
            className={`risk-pill risk-pill--${pestRisk}`}
            onClick={() => handleRiskClick(day.date, 'pest', day.pest_risk)}
          >
            Pest: {day.pest_risk || "LOW (< 10%)"}
          </button>
          </div>
          );
        })}
      </div>
    </div>
  );
}
